import {
  Button,
  Command,
  CommandGroup,
  CommandItem,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@ais-ui/ui-core';
import { PiBoat, PiPath } from 'react-icons/pi';
import React, { useEffect, useState } from 'react';
import { cn } from '@ais-ui/utils';
import { useTargetsOnMap, BULK_TRACKS_MAX } from '../TargetsOnMapContext';
import { useMapPanelContext } from '../MapPanel/MapPanelContext';
import { CENTERCOMPONENTS } from '../MapPanel/CenterComponents';
import { useLocalization } from '@ais-ui/locale-management';
import { useShowAllTracks } from './useShowAllTracks';
import { useToast } from '@ais-ui/ui-core';

interface TargetsOnMapProps {
  className?: string;
}

export const TargetsOnMap = React.memo(({ className }: TargetsOnMapProps) => {
  const { onGetTargets, total } = useTargetsOnMap();
  const { centerComponent, setCenterComponent } = useMapPanelContext();
  const { messages } = useLocalization();
  const { toggleBulkTracks, isBulkTracksActive } = useShowAllTracks();
  const { toast } = useToast();
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (isOpen) {
      onGetTargets();
    }
  }, [isOpen, onGetTargets]);

  const tracksDisabled = !isBulkTracksActive && total > BULK_TRACKS_MAX;

  const tracksDisabledReason = tracksDisabled
    ? `Showing all tracks is limited to ${BULK_TRACKS_MAX} targets. There are currently ${total} targets on the map — narrow the list to ${BULK_TRACKS_MAX} or fewer to enable this.`
    : undefined;

  const onShowTargetsOnMap = async () => {
    setIsOpen(false);
    if (centerComponent === CENTERCOMPONENTS.TARGETSONMAP) {
      setCenterComponent(CENTERCOMPONENTS.NONE);
    } else {
      setCenterComponent(CENTERCOMPONENTS.TARGETSONMAP);
      await onGetTargets();
    }
  };

  const onShowVectorTracks = async () => {
    setIsOpen(false);
    const targets = await onGetTargets();
    if (!isBulkTracksActive && targets.length > BULK_TRACKS_MAX) {
      toast({
        title: messages.genericToastTitleWarning,
        description: `Showing all tracks is limited to ${BULK_TRACKS_MAX} targets. There are currently ${targets.length} targets on the map — narrow the list to ${BULK_TRACKS_MAX} or fewer.`,
        variant: 'warning',
      });
      return;
    }
    toggleBulkTracks(targets);
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <Button
                id="targets-on-map-button"
                variant="outline"
                size="icon"
                className={cn(
                  'relative flex items-center justify-center size-8 p-1 group/btn rounded-lg',
                  className,
                )}
              >
                <PiBoat className="size-5 group-hover/btn:text-primary" />
              </Button>
            </PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent side="right" sideOffset={4}>
            {messages.targetsOnMapTooltip}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      <PopoverContent
        side="right"
        align="start"
        className="w-fit p-0 shadow-sm"
      >
        <Command className="w-fit">
          <CommandGroup>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="block">
                    <CommandItem
                      onSelect={onShowVectorTracks}
                      disabled={tracksDisabled}
                      className="whitespace-nowrap"
                    >
                      <PiPath className="mr-2 size-4" />
                      {isBulkTracksActive
                        ? messages.targetsOnMapHideTracks
                        : `${messages.targetsOnMapShowTracks} (${total})`}
                    </CommandItem>
                  </span>
                </TooltipTrigger>
                {tracksDisabledReason && (
                  <TooltipContent side="right" className="max-w-64">
                    {tracksDisabledReason}
                  </TooltipContent>
                )}
              </Tooltip>
            </TooltipProvider>
            <CommandItem onSelect={onShowTargetsOnMap}>
              <PiBoat className="mr-2 size-4" />
              {messages.targetsOnMapShowTable}
            </CommandItem>
          </CommandGroup>
        </Command>
      </PopoverContent>
    </Popover>
  );
});

TargetsOnMap.displayName = 'TargetsOnMap';
